import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge } from '@shared/ui';
import { cn } from '@shared/lib/cn';
import {
  notificationDeepLink,
  useMarkNotificationReadMutation,
  type NotificationView,
} from '@entities/notification';
import { notificationLabelKey, notificationTone } from '../lib/format';

interface NotificationItemProps {
  notification: NotificationView;
  onAfterAction?: () => void;
  compact?: boolean;
}

export function NotificationItem({ notification, onAfterAction, compact }: NotificationItemProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const markRead = useMarkNotificationReadMutation();
  const link = notificationDeepLink(notification);

  const handleClick = () => {
    if (!notification.read) markRead.mutate(notification.id);
    if (link) navigate(link);
    onAfterAction?.();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'flex w-full flex-col items-start gap-1 rounded-md border bg-background px-3 py-2 text-left transition-colors hover:bg-accent/40',
        !notification.read && 'border-primary/30 bg-primary/5',
        compact && 'px-3 py-2',
      )}
    >
      <div className="flex w-full items-center justify-between gap-2">
        <Badge tone={notificationTone(notification.type)}>
          {t(notificationLabelKey(notification.type))}
        </Badge>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
          {new Date(notification.createdAt).toLocaleString()}
        </span>
      </div>
      <p className="text-sm font-medium">{notification.title}</p>
      {notification.message ? (
        <p className="text-xs text-muted-foreground">{notification.message}</p>
      ) : null}
    </button>
  );
}
